import React, { useEffect, useRef } from 'react'
import { CheckboxMarked, CheckboxUnmarked } from '@components/icons'
import styles from './CheckboxFilterGroup.module.sass'
import { FilterValueType } from '@utils/types'

interface Props {
  attributo?: any
  filters?: any
  setFilters?: any
  className?: string
}

const CheckboxFilterGroup = (props: Props) => {
  const { attributo = undefined, filters = [], setFilters = undefined, className = '' } = props

  const ref = useRef(null)

  useEffect(() => {
    attributo.valori.forEach((val: FilterValueType) => {
      if (val.valori && val.valori.length > 0) {
        if (ref?.current) {
          const hasAnyChild = isAnyChildSelected(val.valori)
          const hasAllChildren = areAllChildrenSelected(val.valori)
          ref.current.indeterminate =
            hasAnyChild &&
            !hasAllChildren &&
            !filters.find((x) => x.id === val.id && x.chiave === val.chiave)
        }
      }
    })
  }, [filters, attributo])

  const areAllChildrenSelected = (childValues: FilterValueType[]) => {
    if (!childValues || childValues.length === 0) return false

    const check = childValues.every((child) =>
      filters.find((x) => x.id === child.id && x.chiave === child.chiave)
    )
    return check
  }

  const isAnyChildSelected = (childValues: FilterValueType[]) => {
    if (!childValues || childValues.length === 0) return false

    const check = childValues.some((child) =>
      filters.find((x) => x.id === child.id && x.chiave === child.chiave)
    )
    return check
  }

  const uniqueByKey = (tempFilters) => {
    return [...new Map(tempFilters.map((item) => [item['id'] + item['chiave'], item])).values()]
  }

  const handleChange = (
    value: FilterValueType,
    category: string,
    isParent: boolean = false,
    childValues?: FilterValueType[]
  ) => {
    if (!setFilters) return

    const valueWithCategory = { ...value, chiave: category }

    if (isParent && childValues && childValues.length > 0) {
      // Handle parent checkbox click (only for items that have children)
      if (filters.find((x) => x.id === value.id && x.chiave === category)) {
        // If unchecking parent, remove both parent and all children
        setFilters(
          filters.filter((f) => {
            // Keep items that are not the parent and not its children (considering category)
            const isNotParent = !(f.id === value.id && f.chiave === category)
            const isNotChild = !childValues.some((child) => child.id === f.id)
            return isNotParent && isNotChild
          })
        )
      } else {
        // If checking parent, add both parent and all children
        setFilters(uniqueByKey([...filters, valueWithCategory, ...childValues]))
      }
    } else {
      // Handle single checkbox click
      if (filters.find((x) => x.id === value.id && x.chiave === category)) {
        setFilters(
          uniqueByKey(filters.filter((f) => !(f.id === value.id && f.chiave === category)))
        )
      } else {
        setFilters(uniqueByKey([...filters, valueWithCategory]))
      }
    }
  }

  return (
    <div className={`${styles.root} ${className}`}>
      {attributo.valori.length > 0 &&
        attributo.valori.map((item: FilterValueType) => {
          const uniqueKey = `${attributo.chiave}-${item.id}`
          return (
            <div
              key={uniqueKey}
              className={`${styles.item} ${item.valori?.length ? styles['item--parent'] : ''}`}
            >
              <label htmlFor={uniqueKey} className={styles.inputWrap}>
                <input
                  type="checkbox"
                  id={uniqueKey}
                  ref={ref}
                  onChange={() => {
                    handleChange(
                      item,
                      attributo.chiave,
                      Boolean(item.valori?.length),
                      item.valori || []
                    )
                  }}
                  checked={
                    !!filters.find(
                      (val) => val.id === item.id && val.chiave === attributo.chiave
                    ) || areAllChildrenSelected(item.valori || [])
                  }
                  className={styles.input}
                />
                <span className={styles.styledInput}>
                  {filters.find((val) => val.id === item.id && val.chiave === attributo.chiave) ? (
                    <CheckboxMarked className={styles.icon} />
                  ) : (
                    <CheckboxUnmarked className={styles.iconUnmarked} />
                  )}
                </span>
                <span className={`${styles?.label} ${item.valori ? styles['label--bold'] : ''}`}>
                  {item?.label}
                </span>
              </label>
              {item.valori && (
                <div className={styles.subItemsContainer}>
                  {item.valori.map((subItem: FilterValueType, j) => {
                    const subItemKey = `${attributo.chiave}-${item.id}-${subItem.id}`
                    return (
                      <label htmlFor={subItemKey} key={subItemKey} className={styles.inputWrap}>
                        <input
                          type="checkbox"
                          id={subItemKey}
                          onChange={() => handleChange(subItem, subItem.chiave)}
                          checked={
                            !!filters.find(
                              (val) => val.id === subItem.id && val.chiave === subItem.chiave
                            )
                          }
                        />
                        <span className={styles.styledInput}>
                          {!!filters.find(
                            (val) => val.id === subItem.id && val.chiave === subItem.chiave
                          ) ? (
                            <CheckboxMarked />
                          ) : (
                            <CheckboxUnmarked />
                          )}
                        </span>
                        <span className={styles.label}>{subItem?.label}</span>
                      </label>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
    </div>
  )
}

export default CheckboxFilterGroup
